import React, { useState, useEffect, useMemo } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import Dashboard from "./components/dashboard/Dashboard";
import SendEnvelope from "./components/dashboard/SendEnvelope";
import AddFieldsEditor from "./components/dashboard/AddFieldsEditor";
import SignDocument from "./components/dashboard/SignDocument";
import EnvelopeSummary from "./components/dashboard/EnvelopeSummary";
import PdfViewer from "./components/dashboard/PdfViewer";
import ManageUsers from "./components/dashboard/ManageUsers";
import Login from "./components/Login";
import { getEnvelope, getMe, getToken, clearToken, getCompanyOverrides } from "./api";
import { COMPANIES, setLogoOverrides } from "./companies";

function App() {
  const [dark, setDark] = useState(false);
  const [themeAnim, setThemeAnim] = useState(false);
  const [envelope, setEnvelope] = useState(null);
  const [user, setUser] = useState(undefined); // undefined = checking
  const [company, setCompany] = useState(null);
  const [prevCompany, setPrevCompany] = useState(null);
  const [logoVersion, setLogoVersion] = useState(0);

  const refreshLogos = React.useCallback(
    () =>
      getCompanyOverrides()
        .then((m) => {
          setLogoOverrides(m);
          setLogoVersion((v) => v + 1);
        })
        .catch(() => {}),
    []
  );

  useEffect(() => {
    refreshLogos();
  }, [refreshLogos]);

  const allowedCompanies = useMemo(
    () =>
      user && user.companies
        ? COMPANIES.filter((c) => user.companies.includes(c.key))
        : [],
    [user]
  );

  useEffect(() => {
    getEnvelope()
      .then(setEnvelope)
      .catch(() => {});
  }, []);

  // Check existing session
  useEffect(() => {
    if (!getToken()) {
      setUser(null);
      return;
    }
    getMe()
      .then(setUser)
      .catch(() => {
        clearToken();
        setUser(null);
      });
  }, []);

  // Keep the `dark` class on <html> so portaled menus/dialogs inherit dark styles
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  // Default company = first company the user can access
  useEffect(() => {
    if (user && user.companies) {
      setCompany(COMPANIES.find((c) => user.companies.includes(c.key)) || null);
      setPrevCompany(null);
    }
  }, [user]);

  const handleAction = (message) => {
    toast(message, {
      style: dark
        ? { background: "#1A1721", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }
        : { background: "#FFFFFF", color: "#1A1815", border: "1px solid #E7E0D8" },
    });
  };

  const toggleTheme = () => {
    setThemeAnim(true);
    setDark((d) => !d);
    window.clearTimeout(window.__themeTimer);
    window.__themeTimer = window.setTimeout(() => setThemeAnim(false), 2350);
  };

  const switchCompany = (c) => {
    if (!c || c.key === company?.key) return;
    setPrevCompany(company);
    setCompany(c);
    handleAction(`Switched to ${c.name}`);
  };

  const goToPrevious = () => {
    if (prevCompany) switchCompany(prevCompany);
  };

  const handleLogin = (u) => setUser(u);
  const handleLogout = () => {
    clearToken();
    setUser(null);
    setCompany(null);
    handleAction("Signed out");
  };

  const accent = company ? company.accent : "#F58220";

  const dashboardProps = {
    onAction: handleAction,
    dark,
    toggleTheme,
    envelope,
    company,
    onSwitchCompany: switchCompany,
    prevCompany,
    onGoToPrevious: goToPrevious,
    companies: allowedCompanies,
    user,
    onLogout: handleLogout,
  };

  const requireAuth = (element) => {
    if (user === undefined) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#8B8680] dark:text-white/50" />
        </div>
      );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (!company) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#8B8680] dark:text-white/50" />
        </div>
      );
    }
    return element;
  };

  return (
    <div
      className={`app-root ${dark ? "dark" : ""} ${themeAnim ? "theme-anim" : ""}`}
      style={{ "--accent": accent }}
    >
      <div className="theme-bg-dark" />
      <div className="glow-blob glow-warm" />
      <BrowserRouter>
        <div className="relative z-10 mx-auto max-w-[1600px] pb-10">
          <Routes>
            <Route
              path="/login"
              element={
                user ? (
                  <Navigate to="/" replace />
                ) : (
                  <Login
                    dark={dark}
                    toggleTheme={toggleTheme}
                    onLogin={handleLogin}
                    onAction={handleAction}
                  />
                )
              }
            />
            <Route path="/" element={requireAuth(<Dashboard {...dashboardProps} />)} />
            <Route
              path="/send"
              element={requireAuth(<SendEnvelope onAction={handleAction} dark={dark} user={user} company={company} />)}
            />
            <Route
              path="/add-fields"
              element={requireAuth(<AddFieldsEditor onAction={handleAction} dark={dark} user={user} company={company} />)}
            />
            <Route
              path="/sign/:id"
              element={requireAuth(<SignDocument onAction={handleAction} dark={dark} user={user} company={company} />)}
            />
            <Route
              path="/envelope/:id"
              element={requireAuth(<EnvelopeSummary onAction={handleAction} user={user} company={company} />)}
            />
            <Route
              path="/view/:id"
              element={requireAuth(<PdfViewer onAction={handleAction} />)}
            />
            <Route
              path="/admin/users"
              element={requireAuth(
                user && !user.is_admin ? (
                  <Navigate to="/" replace />
                ) : (
                  <ManageUsers dark={dark} user={user} onAction={handleAction} refreshLogos={refreshLogos} />
                )
              )}
            />
          </Routes>
        </div>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
